import React from 'react';
import { Shield, ShieldCheck, Download, Calendar, CheckCircle2, Clock, Loader2, PenLine, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useTenant } from './TenantContext';

const TenantDeposits = () => {
  const { profile, securityDeposits, downloading, formatCurrency, formatDate, handleDownloadDepositPdf, openSignModal } = useTenant();
  return (
    <>
  <Card>
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#1B3A4B]" />
        Mes Dépôts de Garantie
      </CardTitle>
    </CardHeader>
    <CardContent>
      {securityDeposits.length > 0 ? (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-700">Actifs</p>
              <p className="text-2xl font-bold text-green-800">
                {formatCurrency(
                  securityDeposits
                    .filter(d => d.status === 'actif')
                    .reduce((sum, d) => sum + (d.amount || 0), 0),
                  profile?.property?.currency || 'XOF'
                )}
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-700">Restitués</p>
              <p className="text-2xl font-bold text-blue-800">
                {formatCurrency(
                  securityDeposits
                    .filter(d => d.status === 'restitue')
                    .reduce((sum, d) => sum + (d.restitution_amount || d.amount || 0), 0),
                  profile?.property?.currency || 'XOF'
                )}
              </p>
            </div>
          </div>

          {/* Deposits List */}
          {securityDeposits.map((deposit) => (
            <div
              key={deposit.id}
              className={`p-4 rounded-lg border ${
                deposit.status === 'actif'
                  ? 'border-green-200 bg-green-50'
                  : deposit.status === 'restitue'
                  ? 'border-blue-200 bg-blue-50'
                  : 'border-orange-200 bg-orange-50'
              }`}
              data-testid={`deposit-item-${deposit.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-500">
                    {deposit.reference}
                  </span>
                  <Badge className={
                    deposit.status === 'actif'
                      ? 'bg-green-100 text-green-700'
                      : deposit.status === 'restitue'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-orange-100 text-orange-700'
                  }>
                    {deposit.status === 'actif' ? 'Actif' : 
                     deposit.status === 'restitue' ? 'Restitué' : 'Retenu'}
                  </Badge>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadDepositPdf(deposit.id, deposit.reference)}
                  disabled={downloading === `deposit-${deposit.id}`}
                  data-testid={`download-deposit-${deposit.id}`}
                >
                  <Download className={`w-4 h-4 ${downloading === `deposit-${deposit.id}` ? 'animate-pulse' : ''}`} />
                  <span className="hidden sm:inline ml-2">Attestation</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openSignModal('security_deposit', deposit.id, `Dépôt ${deposit.reference}`)}
                  className="border-[#1B3A4B] text-[#1B3A4B] hover:bg-[#1B3A4B]/10"
                  data-testid={`sign-deposit-${deposit.id}`}
                >
                  <PenLine className="w-4 h-4" />
                  <span className="hidden sm:inline ml-2">Signer</span>
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Montant versé</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(deposit.amount, deposit.currency || 'XOF')}
                  </p>
                  <p className="text-xs text-gray-500">{deposit.months_covered} mois de caution</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date de versement</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(deposit.deposit_date)}
                  </p>
                </div>
              </div>

              {deposit.status === 'restitue' && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <div className="flex items-center gap-2 text-blue-700">
                    <RotateCcw className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Restitué: {formatCurrency(deposit.restitution_amount || deposit.amount, deposit.currency || 'XOF')}
                    </span>
                  </div>
                  {deposit.restitution_notes && (
                    <p className="text-sm text-blue-600 mt-1 italic">
                      {deposit.restitution_notes}
                    </p>
                  )}
                </div>
              )}

              {deposit.notes && (
                <p className="mt-2 text-sm text-gray-600 italic">
                  {deposit.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">Aucun dépôt de garantie enregistré</p>
        </div>
      )}
    </CardContent>
  </Card>
    </>
  );
};

export default TenantDeposits;
